import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useHasAnyRole } from "@/auth/AuthProvider"
import { ROLES_TECHNIK_LOESCHEN, ROLES_TECHNIK_SCHREIBEN } from "@/auth/permissions"
import { AmpelBadge } from "@/components/AmpelBadge"
import { Badge, Button, Input, Select, Textarea } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import { Sheet, SheetContent, SHEET_WIDTH } from "@/components/Sheet"
import { useToast, fehlerNachricht } from "@/lib/toast"
import { ObjektDetailContent } from "./ObjektDetailPage"
import {
  createAnlage,
  createKomponente,
  createObjekt,
  deleteAnlage,
  deleteAnlageBild,
  fetchAnlage,
  fetchAnlageBilder,
  fetchHierarchie,
  fetchPruefungen,
  fetchPruefungsarten,
  fetchTagBuchstaben,
  fetchWartungen,
  fetchWartungsarten,
  updateAnlage,
  uploadAnlageBild,
  type AnlageEingabe,
  type HierarchieKomponente,
  type HierarchieObjekt,
  type Pruefung,
  type Wartung,
} from "@/lib/api"

const PROZESSARTEN = ["chemisch", "mechanisch", "infrastruktur", "sonstige"]
const REFERENZ_TYPEN = ["anlagenkomplex", "anlage", "teilanlage"]

const LEERE_ANLAGE: AnlageEingabe = {
  bezeichnung: "",
  kostenstelleNavision: "",
  tagUnit: null,
  tagUnitBeschreibung: "",
  prozessart: "",
  referenzTyp: "",
  beschreibung: "",
}

type TabKey = "stammdaten" | "objekte" | "komponenten" | "pruefungen" | "wartungen" | "bilder"

// Schwelle aus MASCHINEN_STRUKTURVERGLEICH.md P1: ab ~20 Direktkomponenten
// Suchfeld + Sortierung statt Flachliste.
const KOMPONENTEN_SUCHE_SCHWELLE = 20

/** Anlagen-Detailseite als Tab-Layout (Ninox-Formularmuster, P2) — AP-3.3. */
export function AnlageDetailPage() {
  const params = useParams<{ id: string }>()
  const istNeu = params.id === "neu"
  const anlageId = istNeu ? null : Number(params.id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const darfSchreiben = useHasAnyRole(ROLES_TECHNIK_SCHREIBEN)
  const darfLoeschen = useHasAnyRole(ROLES_TECHNIK_LOESCHEN)
  const toast = useToast()

  const [tab, setTab] = useState<TabKey>("stammdaten")
  const [form, setForm] = useState<AnlageEingabe>(LEERE_ANLAGE)
  const [neuesObjekt, setNeuesObjekt] = useState("")
  const [detailObjektId, setDetailObjektId] = useState<number | null>(null)
  const [neueKomponente, setNeueKomponente] = useState({ beschreibung: "", tagBuchstabeId: "", tagZahl: "", objektId: "" })
  const [komponentenSuche, setKomponentenSuche] = useState("")
  const [komponentenSort, setKomponentenSort] = useState<"tag" | "beschreibung">("tag")

  const { data: anlage } = useQuery({
    queryKey: ["anlagen", anlageId],
    queryFn: () => fetchAnlage(anlageId!),
    enabled: anlageId !== null,
  })

  useEffect(() => {
    if (anlage) setForm(anlage)
  }, [anlage])

  const { data: hierarchie, refetch: refetchHierarchie } = useQuery({
    queryKey: ["hierarchie", anlageId],
    queryFn: () => fetchHierarchie(anlageId!),
    enabled: anlageId !== null,
  })

  const { data: tagBuchstaben } = useQuery({
    queryKey: ["tag-buchstaben"],
    queryFn: fetchTagBuchstaben,
  })

  const { data: bilder, refetch: refetchBilder } = useQuery({
    queryKey: ["anlage-bilder", anlageId],
    queryFn: () => fetchAnlageBilder(anlageId!),
    enabled: anlageId !== null,
  })

  const { data: pruefungen } = useQuery({
    queryKey: ["pruefungen", "anlage", anlageId],
    queryFn: () => fetchPruefungen({ anlageId: anlageId! }),
    enabled: anlageId !== null,
  })
  const { data: pruefungsarten } = useQuery({
    queryKey: ["pruefungsarten"],
    queryFn: fetchPruefungsarten,
  })

  const { data: wartungen } = useQuery({
    queryKey: ["wartungen", "anlage", anlageId],
    queryFn: () => fetchWartungen({ anlageId: anlageId! }),
    enabled: anlageId !== null,
  })
  const { data: wartungsarten } = useQuery({
    queryKey: ["wartungsarten"],
    queryFn: fetchWartungsarten,
  })

  const speichern = useMutation({
    mutationFn: async () => {
      if (istNeu) {
        const neu = await createAnlage(form)
        return neu
      }
      return updateAnlage(anlageId!, form)
    },
    onSuccess: (ergebnis) => {
      queryClient.invalidateQueries({ queryKey: ["anlagen"] })
      toast.success("Anlage gespeichert.")
      if (istNeu) {
        navigate(`/maschinen/anlagen/${ergebnis.id}`)
      } else {
        refetchHierarchie()
      }
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Anlage konnte nicht gespeichert werden.")),
  })

  const loeschen = useMutation({
    mutationFn: () => deleteAnlage(anlageId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["anlagen"] })
      toast.success("Anlage gelöscht.")
      navigate("/maschinen")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Anlage konnte nicht gelöscht werden.")),
  })

  const objektAnlegen = useMutation({
    mutationFn: () =>
      createObjekt({
        anlageId: anlageId!,
        bezeichnung: neuesObjekt,
        beschreibung: null,
        lageplanId: null,
      }),
    onSuccess: () => {
      setNeuesObjekt("")
      toast.success("Objekt angelegt.")
      refetchHierarchie()
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Objekt konnte nicht angelegt werden.")),
  })

  const komponenteAnlegen = useMutation({
    mutationFn: () =>
      createKomponente({
        anlageId: anlageId!,
        objektId: neueKomponente.objektId ? Number(neueKomponente.objektId) : null,
        tagBuchstabeId: neueKomponente.tagBuchstabeId ? Number(neueKomponente.tagBuchstabeId) : null,
        beschreibung: neueKomponente.beschreibung,
        tagZahl: neueKomponente.tagZahl ? Number(neueKomponente.tagZahl) : null,
        identifikation: null,
        erschwerniszulage: null,
        what3words: null,
      }),
    onSuccess: () => {
      setNeueKomponente({ beschreibung: "", tagBuchstabeId: "", tagZahl: "", objektId: "" })
      toast.success("Komponente angelegt.")
      refetchHierarchie()
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Komponente konnte nicht angelegt werden.")),
  })

  const bildHochladen = useMutation({
    mutationFn: (file: File) => uploadAnlageBild(anlageId!, file),
    onSuccess: () => {
      toast.success("Bild hochgeladen.")
      refetchBilder()
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Bild konnte nicht hochgeladen werden.")),
  })

  const bildLoeschen = useMutation({
    mutationFn: (bildId: number) => deleteAnlageBild(bildId),
    onSuccess: () => {
      toast.success("Bild entfernt.")
      refetchBilder()
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Bild konnte nicht entfernt werden.")),
  })

  const pruefungsartName = (id: number) =>
    pruefungsarten?.find((a) => a.id === id)?.pruefungsart ?? `Art #${id}`
  const wartungsartName = (id: number) =>
    wartungsarten?.find((a) => a.id === id)?.titel ?? `Art #${id}`

  const direktKomponenten = hierarchie?.komponenten ?? []
  const gefilterteDirektKomponenten = useMemo(() => {
    let liste = direktKomponenten
    if (komponentenSuche.trim()) {
      const q = komponentenSuche.trim().toLowerCase()
      liste = liste.filter(
        (k) =>
          k.beschreibung.toLowerCase().includes(q) ||
          (k.tagNummer ?? "").toLowerCase().includes(q),
      )
    }
    return [...liste].sort((a, b) =>
      komponentenSort === "tag"
        ? (a.tagNummer ?? "").localeCompare(b.tagNummer ?? "")
        : a.beschreibung.localeCompare(b.beschreibung, "de"),
    )
  }, [direktKomponenten, komponentenSuche, komponentenSort])

  const gesamtKomponenten =
    (hierarchie?.objekte.reduce((sum, o) => sum + o.komponenten.length, 0) ?? 0) +
    direktKomponenten.length

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const objektSpalten = useMemo<ColumnDef<HierarchieObjekt>[]>(
    () => [
      {
        id: "bezeichnung",
        header: "Bezeichnung",
        accessorFn: (o) => o.bezeichnung,
      },
      {
        id: "objektCode",
        header: "Objekt-Code",
        accessorFn: (o) => o.objektCode ?? "",
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.objektCode ?? "—"}</span>
        ),
      },
      {
        id: "komponentenAnzahl",
        header: "Komponenten",
        accessorFn: (o) => o.komponenten.length,
      },
    ],
    [],
  )

  const pruefungSpalten = useMemo<ColumnDef<Pruefung>[]>(
    () => [
      {
        id: "pruefungsart",
        header: "Prüfungsart",
        accessorFn: (p) => pruefungsartName(p.pruefungsartId),
        cell: ({ row }) => (
          <Link
            to={`/maschinen/pruefungen/${row.original.id}`}
            className="block rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            {pruefungsartName(row.original.pruefungsartId)}
          </Link>
        ),
      },
      {
        id: "pruefungsdatum",
        header: "Prüfungsdatum",
        accessorFn: (p) => p.pruefungsdatum ?? "",
        cell: ({ row }) => row.original.pruefungsdatum ?? "—",
      },
      {
        id: "ablaufdatum",
        header: "Ablaufdatum",
        accessorFn: (p) => p.ablaufdatum ?? "",
        cell: ({ row }) => row.original.ablaufdatum ?? "—",
      },
      {
        id: "ampel",
        header: "Ampel",
        accessorFn: (p) => p.ampel,
        cell: ({ row }) => <AmpelBadge ampel={row.original.ampel} />,
      },
    ],
    // pruefungsartName hängt an den Query-Daten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pruefungsarten],
  )

  const wartungSpalten = useMemo<ColumnDef<Wartung>[]>(
    () => [
      {
        id: "wartungsart",
        header: "Wartungs-/Inspektionsart",
        accessorFn: (w) => wartungsartName(w.wartungsartId),
        cell: ({ row }) => (
          <Link
            to={`/maschinen/wartungen/${row.original.id}`}
            className="block rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            {wartungsartName(row.original.wartungsartId)}
          </Link>
        ),
      },
      {
        id: "wartungsdatum",
        header: "Wartungsdatum",
        accessorFn: (w) => w.wartungsdatum ?? "",
        cell: ({ row }) => row.original.wartungsdatum ?? "—",
      },
      {
        id: "ablaufdatum",
        header: "Ablaufdatum",
        accessorFn: (w) => w.ablaufdatum ?? "",
        cell: ({ row }) => row.original.ablaufdatum ?? "—",
      },
      {
        id: "ampel",
        header: "Ampel",
        accessorFn: (w) => w.ampel,
        cell: ({ row }) => <AmpelBadge ampel={row.original.ampel} />,
      },
    ],
    // wartungsartName hängt an den Query-Daten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wartungsarten],
  )

  if (istNeu) {
    return (
      <div>
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">Neue Anlage</h1>
          <p className="mt-1 text-sm text-slate-600">
            Stammdaten (02-A) — TAG-Unit ist die Wurzel für TAG-Nummer/Navision-Code (§4).
          </p>
        </header>
        <div className="max-w-2xl">
          <StammdatenForm
            form={form}
            setForm={setForm}
            istNeu
            darfSchreiben={darfSchreiben}
            darfLoeschen={false}
            speichernPending={speichern.isPending}
            speichernError={speichern.isError}
            onSubmit={() => speichern.mutate()}
            onLoeschen={() => {}}
          />
        </div>
      </div>
    )
  }

  const tabs: { key: TabKey; titel: string; anzahl?: number }[] = [
    { key: "stammdaten", titel: "Stammdaten" },
    { key: "objekte", titel: "Objekte", anzahl: hierarchie?.objekte.length ?? 0 },
    { key: "komponenten", titel: "Komponenten", anzahl: gesamtKomponenten },
    { key: "pruefungen", titel: "Prüfungen", anzahl: pruefungen?.length ?? 0 },
    { key: "wartungen", titel: "Wartungen", anzahl: wartungen?.length ?? 0 },
    { key: "bilder", titel: "Bilder", anzahl: bilder?.length ?? 0 },
  ]

  return (
    <div>
      <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-slate-500" aria-label="Breadcrumb">
        <Link
          to="/maschinen"
          className="rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          Anlagen
        </Link>
        <span aria-hidden="true">›</span>
        <span className="text-slate-700">{anlage?.bezeichnung ?? "Anlage"}</span>
      </nav>

      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">{anlage?.bezeichnung ?? "Anlage"}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {anlage?.tagUnit !== null && anlage?.tagUnit !== undefined
            ? `TAG-Unit ${String(anlage.tagUnit).padStart(3, "0")}`
            : "keine TAG-Unit"}
          {anlage?.kostenstelleNavision ? ` · Kostenstelle ${anlage.kostenstelleNavision}` : ""}
        </p>
      </header>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-nowrap gap-1 overflow-x-auto border-b border-slate-200 px-4 pt-3">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`shrink-0 whitespace-nowrap rounded-t-md border-b-2 px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
                tab === t.key
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.titel}
              {t.anzahl !== undefined ? ` (${t.anzahl})` : ""}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === "stammdaten" && (
            <div className="max-w-2xl">
              <StammdatenForm
                form={form}
                setForm={setForm}
                istNeu={false}
                darfSchreiben={darfSchreiben}
                darfLoeschen={darfLoeschen}
                speichernPending={speichern.isPending}
                speichernError={speichern.isError}
                onSubmit={() => speichern.mutate()}
                onLoeschen={() => loeschen.mutate()}
              />
            </div>
          )}

          {tab === "objekte" && (
            <div className="space-y-4">
              <DataGrid
                columns={objektSpalten}
                data={hierarchie?.objekte ?? []}
                storageKey="anlage-detail-objekte"
                emptyMessage="Keine Objekte dieser Anlage zugeordnet."
                onRowClick={(o) => setDetailObjektId(o.id)}
              />

              {darfSchreiben && (
                <form
                  className="flex gap-2 border-t border-slate-200 pt-4"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (neuesObjekt.trim()) objektAnlegen.mutate()
                  }}
                >
                  <Input
                    placeholder="Neues Objekt…"
                    value={neuesObjekt}
                    onChange={(e) => setNeuesObjekt(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" variant="secondary">
                    + Objekt
                  </Button>
                </form>
              )}
            </div>
          )}

          {tab === "komponenten" && (
            <div className="space-y-6">
              {hierarchie?.objekte.map((objekt) => (
                <div key={objekt.id}>
                  <button
                    type="button"
                    onClick={() => setDetailObjektId(objekt.id)}
                    className="mb-2 inline-block rounded-md font-medium text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                  >
                    {objekt.bezeichnung} ({objekt.komponenten.length})
                  </button>
                  <KomponentenTabelle komponenten={objekt.komponenten} />
                </div>
              ))}

              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-medium text-slate-900">
                    Direkt der Anlage zugeordnet ({direktKomponenten.length})
                  </h3>
                  {direktKomponenten.length >= KOMPONENTEN_SUCHE_SCHWELLE && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        placeholder="Suche Beschreibung/TAG-Nummer…"
                        value={komponentenSuche}
                        onChange={(e) => setKomponentenSuche(e.target.value)}
                        className="w-64"
                      />
                      <Select
                        value={komponentenSort}
                        onChange={(e) => setKomponentenSort(e.target.value as "tag" | "beschreibung")}
                        className="w-auto"
                      >
                        <option value="tag">Sortierung: TAG-Nummer</option>
                        <option value="beschreibung">Sortierung: Beschreibung</option>
                      </Select>
                    </div>
                  )}
                </div>
                <div
                  className={
                    direktKomponenten.length >= KOMPONENTEN_SUCHE_SCHWELLE
                      ? "max-h-[480px] overflow-y-auto rounded-md border border-slate-100"
                      : ""
                  }
                >
                  <KomponentenTabelle komponenten={gefilterteDirektKomponenten} />
                </div>
                {komponentenSuche && gefilterteDirektKomponenten.length === 0 && (
                  <p className="py-4 text-center text-sm text-slate-400">
                    Keine Komponenten für „{komponentenSuche}" gefunden.
                  </p>
                )}
              </div>

              {darfSchreiben && (
                <form
                  className="space-y-2 rounded-md bg-slate-50 p-3"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (neueKomponente.beschreibung.trim()) komponenteAnlegen.mutate()
                  }}
                >
                  <div className="text-xs font-medium text-slate-500">Neue Komponente</div>
                  <Input
                    placeholder="Beschreibung"
                    value={neueKomponente.beschreibung}
                    onChange={(e) =>
                      setNeueKomponente({ ...neueKomponente, beschreibung: e.target.value })
                    }
                    className="w-full"
                  />
                  <div className="flex gap-2">
                    <Select
                      value={neueKomponente.objektId}
                      onChange={(e) =>
                        setNeueKomponente({ ...neueKomponente, objektId: e.target.value })
                      }
                      className="flex-1"
                    >
                      <option value="">Direkt an Anlage</option>
                      {hierarchie?.objekte.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.bezeichnung}
                        </option>
                      ))}
                    </Select>
                    <Select
                      value={neueKomponente.tagBuchstabeId}
                      onChange={(e) =>
                        setNeueKomponente({ ...neueKomponente, tagBuchstabeId: e.target.value })
                      }
                      className="flex-1"
                    >
                      <option value="">TAG-Buchstabe…</option>
                      {tagBuchstaben?.map((tb) => (
                        <option key={tb.id} value={tb.id}>
                          {tb.tagBuchstabe}
                        </option>
                      ))}
                    </Select>
                    <Input
                      type="number"
                      placeholder="TAG-Zahl"
                      value={neueKomponente.tagZahl}
                      onChange={(e) =>
                        setNeueKomponente({ ...neueKomponente, tagZahl: e.target.value })
                      }
                      className="w-24"
                    />
                  </div>
                  <Button type="submit" variant="secondary">
                    + Komponente
                  </Button>
                </form>
              )}
            </div>
          )}

          {tab === "pruefungen" && (
            <DataGrid
              columns={pruefungSpalten}
              data={pruefungen ?? []}
              storageKey="anlage-detail-pruefungen"
              emptyMessage="Keine Prüfungen für diese Anlage erfasst."
            />
          )}

          {tab === "wartungen" && (
            <DataGrid
              columns={wartungSpalten}
              data={wartungen ?? []}
              storageKey="anlage-detail-wartungen"
              emptyMessage="Keine Wartungen für diese Anlage erfasst."
            />
          )}

          {tab === "bilder" && (
            <div>
              <p className="mb-3 text-sm text-slate-600">
                02-KB — Upload verknüpft das Bild sofort mit der Anlage (Bugfix fehlende
                Rückverknüpfung, `03_logik.md` Regel 33).
              </p>
              {darfSchreiben && (
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) bildHochladen.mutate(file)
                    e.target.value = ""
                  }}
                  className="mb-4 rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                />
              )}
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {bilder?.map((b) => (
                  <li
                    key={b.id}
                    className="flex flex-col justify-between rounded-md border border-slate-200 p-2 text-xs"
                  >
                    <span className="truncate text-slate-600">
                      {b.bildbeschreibung ?? b.paperlessId}
                    </span>
                    {darfSchreiben && (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => bildLoeschen.mutate(b.id)}
                        className="mt-2 h-auto self-start bg-transparent px-0 py-0 text-red-600 hover:bg-transparent hover:underline"
                      >
                        Entfernen
                      </Button>
                    )}
                  </li>
                ))}
                {bilder?.length === 0 && (
                  <li className="col-span-full text-slate-400">Keine Bilder hinterlegt.</li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Objekt-Detail-Sheet von rechts (UX_KONZEPT.md §7) — Zeilen-Klick in der
          Objekte-Kindliste öffnet statt zu navigieren; `/maschinen/objekte/:id`
          bleibt als Deep-Link-Fallback (ObjektDetailPage). */}
      <Sheet open={detailObjektId !== null} onOpenChange={(open) => !open && setDetailObjektId(null)}>
        <SheetContent className="overflow-y-auto" sheetKey="objekt-detail" defaultWidth={SHEET_WIDTH.xl}>
          {detailObjektId !== null && <ObjektDetailContent objektId={detailObjektId} />}
        </SheetContent>
      </Sheet>
    </div>
  )
}

/** Spalten der eingebetteten Komponenten-Listen (statisch, daher auf Modulebene). */
const KOMPONENTEN_SPALTEN: ColumnDef<HierarchieKomponente>[] = [
  {
    id: "beschreibung",
    header: "Beschreibung",
    accessorFn: (k) => k.beschreibung,
    cell: ({ row }) => (
      <Link
        to={`/maschinen/komponenten/${row.original.id}`}
        className="flex items-center rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
      >
        {row.original.beschreibung}
        <What3WordsLink wert={row.original.what3words} />
      </Link>
    ),
  },
  {
    id: "tagNummer",
    header: "TAG-Nummer",
    accessorFn: (k) => k.tagNummer ?? "",
    cell: ({ row }) => (
      <span className="font-mono text-xs">{row.original.tagNummer ?? "—"}</span>
    ),
  },
  {
    id: "status",
    header: "Status",
    accessorFn: (k) => (k.gesperrt ? "gesperrt" : "frei"),
    cell: ({ row }) => (
      <Badge tone={row.original.gesperrt ? "red" : "green"}>
        {row.original.gesperrt ? "gesperrt" : "frei"}
      </Badge>
    ),
  },
]

function KomponentenTabelle({ komponenten }: { komponenten: HierarchieKomponente[] }) {
  return (
    <DataGrid
      columns={KOMPONENTEN_SPALTEN}
      data={komponenten}
      storageKey="anlage-detail-komponenten"
      emptyMessage="keine Komponenten"
    />
  )
}

interface StammdatenFormProps {
  form: AnlageEingabe
  setForm: (form: AnlageEingabe) => void
  istNeu: boolean
  darfSchreiben: boolean
  darfLoeschen: boolean
  speichernPending: boolean
  speichernError: boolean
  onSubmit: () => void
  onLoeschen: () => void
}

function StammdatenForm({
  form,
  setForm,
  istNeu,
  darfSchreiben,
  darfLoeschen,
  speichernPending,
  speichernError,
  onSubmit,
  onLoeschen,
}: StammdatenFormProps) {
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <label className="block text-sm">
        Bezeichnung
        <Input
          required
          value={form.bezeichnung}
          onChange={(e) => setForm({ ...form, bezeichnung: e.target.value })}
          className="mt-1"
        />
      </label>
      <label className="block text-sm">
        TAG-Unit
        <Input
          type="number"
          value={form.tagUnit ?? ""}
          onChange={(e) => setForm({ ...form, tagUnit: e.target.value ? Number(e.target.value) : null })}
          className="mt-1"
        />
      </label>
      <label className="block text-sm">
        TAG-Unit-Beschreibung
        <Input
          value={form.tagUnitBeschreibung ?? ""}
          onChange={(e) => setForm({ ...form, tagUnitBeschreibung: e.target.value })}
          className="mt-1"
        />
      </label>
      <label className="block text-sm">
        Kostenstelle (Navision)
        <Input
          value={form.kostenstelleNavision ?? ""}
          onChange={(e) => setForm({ ...form, kostenstelleNavision: e.target.value })}
          className="mt-1"
        />
      </label>
      <label className="block text-sm">
        Prozessart
        <Select
          value={form.prozessart ?? ""}
          onChange={(e) => setForm({ ...form, prozessart: e.target.value })}
          className="mt-1"
        >
          <option value="">—</option>
          {PROZESSARTEN.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </Select>
      </label>
      <label className="block text-sm">
        Referenz-Typ
        <Select
          value={form.referenzTyp ?? ""}
          onChange={(e) => setForm({ ...form, referenzTyp: e.target.value })}
          className="mt-1"
        >
          <option value="">—</option>
          {REFERENZ_TYPEN.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </label>
      <label className="block text-sm">
        Beschreibung
        <Textarea
          value={form.beschreibung ?? ""}
          onChange={(e) => setForm({ ...form, beschreibung: e.target.value })}
          className="mt-1"
          rows={3}
        />
      </label>

      <div className="flex items-center gap-3 pt-2">
        {darfSchreiben && (
          <Button type="submit" variant="primary" disabled={speichernPending}>
            Speichern
          </Button>
        )}
        {!istNeu && darfLoeschen && (
          <Button type="button" variant="destructive" onClick={onLoeschen}>
            Löschen
          </Button>
        )}
        {speichernError && (
          <span className="text-sm text-red-700">
            Speichern fehlgeschlagen (Berechtigung? Pflichtfelder?)
          </span>
        )}
      </div>
    </form>
  )
}

/**
 * 02-Regel 4: what3words-Kartenlink je Komponente. Der U2-Bug (kaputte
 * Ninox-Kartenintegration) wird bewusst NICHT übernommen — reiner Link zu
 * what3words.com statt eingebettetem iframe/Leaflet-Widget (ABDECKUNG_02.md).
 */
function What3WordsLink({ wert }: { wert: string | null }) {
  if (!wert) return null
  const bereinigt = wert.replace(/^\/+/, "")
  return (
    <a
      href={`https://what3words.com/${bereinigt}`}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`what3words: ${wert}`}
      className="ml-1.5 rounded-md text-xs text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
    >
      📍
    </a>
  )
}
